function resolveApiBaseUrl() {
  const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/$/, '');
  }

  if (import.meta.env.DEV || typeof window === 'undefined') {
    return '/api/v1';
  }

  const productionHosts = new Set(['aegiswebshield.com', 'www.aegiswebshield.com', 'staging.aegiswebshield.com']);
  if (productionHosts.has(window.location.hostname)) {
    return '/api/v1';
  }

  return 'https://aegiswebshield.com/api/v1';
}

const API_BASE_URL = resolveApiBaseUrl();

const TOKEN_KEY = 'aegis_access_token';
const REFRESH_TOKEN_KEY = 'aegis_refresh_token';
const AUTH_REQUIRED_EVENT = 'aegis:auth-required';

function koreanApiErrorMessage(message, status) {
  const normalized = String(message || '').trim();
  if (!normalized) return `API 요청에 실패했습니다. 상태 코드: ${status}`;

  const lower = normalized.toLowerCase();
  if (lower.includes('invalid credentials')) return '이메일 또는 비밀번호가 올바르지 않습니다.';
  if (lower.includes('invalid login payload')) return '로그인 입력값을 확인해 주세요.';
  if (lower.includes('invalid registration payload')) return '회원가입 입력값을 확인해 주세요.';
  if (lower.includes('email already exists')) return '이미 가입된 이메일입니다.';
  if (lower.includes('authentication is required') || lower.includes('unauthorized')) return '로그인이 필요합니다.';
  if (lower.includes('forbidden') || lower.includes('permission')) return '요청을 처리할 권한이 없습니다.';
  if (lower.includes('not found')) return '요청한 정보를 찾을 수 없습니다.';
  if (lower.includes('plan limit reached')) {
    if (lower.includes('monthly scan')) return '이번 달 스캔 생성 한도에 도달했습니다. 플랜을 확인하거나 업그레이드를 요청해 주세요.';
    if (lower.includes('monthly ai analysis')) return '이번 달 AI 수정 가이드 생성 한도에 도달했습니다. 플랜을 확인하거나 업그레이드를 요청해 주세요.';
    if (lower.includes('monthly recheck report')) return '이번 달 재검증 리포트 생성 한도에 도달했습니다. 플랜을 확인하거나 업그레이드를 요청해 주세요.';
    if (lower.includes('monthly report')) return '이번 달 리포트 생성 한도에 도달했습니다. 플랜을 확인하거나 업그레이드를 요청해 주세요.';
    if (lower.includes('project')) return '프로젝트 생성 한도에 도달했습니다. 플랜을 확인하거나 업그레이드를 요청해 주세요.';
    if (lower.includes('target')) return '대상 등록 한도에 도달했습니다. 플랜을 확인하거나 업그레이드를 요청해 주세요.';
    if (lower.includes('member')) return '멤버 초대 한도에 도달했습니다. 플랜을 확인하거나 업그레이드를 요청해 주세요.';
    return '플랜 한도에 도달했습니다. 플랜을 확인하거나 업그레이드를 요청해 주세요.';
  }
  if (lower.includes('scan request limit reached')) {
    return '시간당 스캔 생성 한도에 도달했습니다. 잠시 후 다시 시도하거나 플랜 업그레이드를 요청해 주세요.';
  }
  if (lower.includes('organization active scan limit reached')) {
    return '조직의 동시 스캔 한도에 도달했습니다. 진행 중인 스캔이 끝난 뒤 다시 시도하거나 플랜을 확인해 주세요.';
  }
  if (lower.includes('only one queued or active scan')) {
    return '같은 대상에 이미 대기 또는 실행 중인 스캔이 있습니다. 진행 중 스캔 화면에서 상태를 확인해 주세요.';
  }
  if (lower.includes('target must be marked as scan_authorized')) {
    return '스캔 승인된 대상만 실행할 수 있습니다. 대상 URL/API 화면에서 승인 근거를 확인해 주세요.';
  }
  if (lower.includes('invalid') && lower.includes('payload')) return '입력값을 확인해 주세요.';
  if (lower.includes('failed to fetch') || lower.includes('network')) return '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.';
  if (lower.startsWith('api request failed with status')) return `API 요청에 실패했습니다. 상태 코드: ${status}`;

  return normalized;
}

export function getStoredToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setStoredTokens({ access_token: accessToken, refresh_token: refreshToken }) {
  sessionStorage.setItem(TOKEN_KEY, accessToken);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearStoredTokens() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

function notifyAuthenticationRequired() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
  }
}

async function request(path, { method = 'GET', body, token = getStoredToken() } = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: body ? JSON.stringify(body) : undefined
    });
  } catch {
    throw new Error('API 서버에 연결할 수 없습니다. 잠시 후 다시 시도하거나 접속 주소를 확인하세요.');
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    const message = payload?.error?.message || '';
    if (response.status === 401 && token) {
      clearStoredTokens();
      notifyAuthenticationRequired();
    }
    throw new Error(koreanApiErrorMessage(message, response.status));
  }

  return payload?.data;
}

export const aegisApi = {
  login: (credentials) => request('/auth/login', { method: 'POST', body: credentials, token: null }),
  register: (account) => request('/auth/register', { method: 'POST', body: account, token: null }),
  acceptInvite: (invite) => request('/auth/accept-invite', { method: 'POST', body: invite, token: null }),
  me: () => request('/me'),
  updateMe: (profile) => request('/me', { method: 'PATCH', body: profile }),
  changePassword: (passwords) => request('/me/change-password', { method: 'POST', body: passwords }),
  securityDashboard: () => request('/dashboard/security'),
  billingUsage: (organizationId) => request(`/billing/usage?organization_id=${encodeURIComponent(organizationId)}`),
  createPlanChangeRequest: (organizationId, request) =>
    request(`/billing/organizations/${encodeURIComponent(organizationId)}/plan-change-requests`, { method: 'POST', body: request }),
  auditLogs: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/audit-logs${queryString ? `?${queryString}` : ''}`);
  },
  organizations: () => request('/organizations'),
  organization: (organizationId) => request(`/organizations/${encodeURIComponent(organizationId)}`),
  updateOrganization: (organizationId, organization) =>
    request(`/organizations/${encodeURIComponent(organizationId)}`, { method: 'PATCH', body: organization }),
  organizationMembers: (organizationId) => request(`/organizations/${encodeURIComponent(organizationId)}/members`),
  addOrganizationMember: (organizationId, member) =>
    request(`/organizations/${encodeURIComponent(organizationId)}/members`, { method: 'POST', body: member }),
  deactivateOrganizationMember: (organizationId, userId) =>
    request(`/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/deactivate`, { method: 'POST' }),
  reinviteOrganizationMember: (organizationId, userId) =>
    request(`/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/reinvite`, { method: 'POST' }),
  platformMe: () => request('/platform/me'),
  platformOrganizations: () => request('/platform/organizations'),
  platformOrganization: (organizationId) => request(`/platform/organizations/${encodeURIComponent(organizationId)}`),
  platformNotificationDeliveries: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/platform/notification-deliveries${queryString ? `?${queryString}` : ''}`);
  },
  platformMailProviderReadiness: () => request('/platform/mail/provider-readiness'),
  platformSupportReadiness: () => request('/platform/support/readiness'),
  platformPlanChangeRequests: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/platform/plan-change-requests${queryString ? `?${queryString}` : ''}`);
  },
  platformPlanChangeRequestImpact: (requestId) =>
    request(`/platform/plan-change-requests/${encodeURIComponent(requestId)}/impact`),
  updatePlatformPlanChangeRequestStatus: (requestId, payload) =>
    request(`/platform/plan-change-requests/${encodeURIComponent(requestId)}/status`, { method: 'PATCH', body: payload }),
  applyPlatformPlanChangeRequest: (requestId, payload) =>
    request(`/platform/plan-change-requests/${encodeURIComponent(requestId)}/apply`, { method: 'POST', body: payload }),
  platformSupportNotes: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/platform/support-notes${queryString ? `?${queryString}` : ''}`);
  },
  platformSupportNoteFollowUps: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/platform/support-notes/follow-ups${queryString ? `?${queryString}` : ''}`);
  },
  platformSupportTickets: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/platform/support-tickets${queryString ? `?${queryString}` : ''}`);
  },
  createPlatformSupportNote: (note) => request('/platform/support-notes', { method: 'POST', body: note }),
  updatePlatformSupportNoteStatus: (noteId, noteStatus) =>
    request(`/platform/support-notes/${encodeURIComponent(noteId)}/status`, { method: 'PATCH', body: { note_status: noteStatus } }),
  archivePlatformSupportNote: (noteId, archiveReason) =>
    request(`/platform/support-notes/${encodeURIComponent(noteId)}/archive`, {
      method: 'POST',
      body: { archive_reason: archiveReason }
    }),
  platformOverview: () => request('/platform/overview'),
  platformScans: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/platform/scans${queryString ? `?${queryString}` : ''}`);
  },
  platformUsers: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/platform/users${queryString ? `?${queryString}` : ''}`);
  },
  updatePlatformUserStatus: (userId, status, reason) =>
    request(`/platform/users/${encodeURIComponent(userId)}/status`, { method: 'PATCH', body: { status, reason } }),
  reinvitePlatformUser: (userId, organizationId, reason) =>
    request(`/platform/users/${encodeURIComponent(userId)}/reinvite`, {
      method: 'POST',
      body: { organization_id: organizationId, reason }
  }),
  platformBillingAccounts: () => request('/platform/billing/accounts'),
  platformRevenueSummary: () => request('/platform/billing/revenue-summary'),
  platformPaymentReadiness: () => request('/platform/billing/payment-readiness'),
  platformManualInvoices: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/platform/billing/manual-invoices${queryString ? `?${queryString}` : ''}`);
  },
  createPaymentCheckoutSession: (organizationId, payload) =>
    request(`/billing/organizations/${encodeURIComponent(organizationId)}/checkout-sessions`, { method: 'POST', body: payload }),
  projects: (organizationId) => request(`/projects${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ''}`),
  createProject: (project) => request('/projects', { method: 'POST', body: project }),
  targets: (projectId) => request(`/targets${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''}`),
  createTarget: (target) => request('/targets', { method: 'POST', body: target }),
  importEndpoints: (payload) => request('/targets/import-endpoints', { method: 'POST', body: payload }),
  scans: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/scans${queryString ? `?${queryString}` : ''}`);
  },
  createScan: (scan) => request('/scans', { method: 'POST', body: scan }),
  startScan: (scanId) => request(`/scans/${encodeURIComponent(scanId)}/start`, { method: 'POST' }),
  scanStatus: (scanId) => request(`/scans/${encodeURIComponent(scanId)}/status`),
  scanLogs: (scanId) => request(`/scans/${encodeURIComponent(scanId)}/logs`),
  cancelScan: (scanId) => request(`/scans/${encodeURIComponent(scanId)}/cancel`, { method: 'POST' }),
  scanQualitySummary: () => request('/scan-quality/summary'),
  scanQualityProfiles: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/scan-quality/profiles${queryString ? `?${queryString}` : ''}`);
  },
  scanQualityProfile: (targetId) => request(`/scan-quality/profiles/${encodeURIComponent(targetId)}`),
  findings: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/findings${queryString ? `?${queryString}` : ''}`);
  },
  finding: (findingId) => request(`/findings/${encodeURIComponent(findingId)}`),
  updateFindingStatus: (findingId, status) =>
    request(`/findings/${encodeURIComponent(findingId)}/status`, { method: 'PATCH', body: { status } }),
  generateAiAnalysis: (findingId) => request(`/findings/${encodeURIComponent(findingId)}/ai-analysis`, { method: 'POST' }),
  aiAnalysis: (analysisId) => request(`/ai-analyses/${encodeURIComponent(analysisId)}`),
  reports: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/reports${queryString ? `?${queryString}` : ''}`);
  },
  createReport: (report) => request('/reports', { method: 'POST', body: report }),
  createRecheckReport: (report) => request('/reports/recheck', { method: 'POST', body: report }),
  report: (reportId) => request(`/reports/${encodeURIComponent(reportId)}`)
};
